import type { Namespace } from '../types';

// Cultivation console (W3 / R-157). DE drafted — queued for native review.
export const cultivation = {
  // R-204 split this console into one screen per URL, so `title`/`desc` (which
  // described all five at once) are gone — each screen has its own heading and its
  // own `desc*` below. These five are the HEADINGS now (the `tab` prefix is
  // historical), and they must stay word-for-word identical to the nav labels in
  // `nav.ts`: with the tab bar gone, the sidebar leaf is the only other place the
  // screen is named, and a sidebar that says one thing while the heading says
  // another is the same defect as a shared description. Renamed here rather than
  // in `nav.ts` because these were the invented-for-a-tab-bar words ("Taxonomy",
  // "Data flags") and the plainer ones read better in both places.
  // R-210: "Categories" became "Catalogue" when the screen stopped being just
  // the category tree — it now carries the whole hierarchy down to varieties.
  tabCatalogue: { de: 'Katalog', en: 'Catalogue' },
  tabProfiles: { de: 'Profile', en: 'Profiles' },
  tabCapacity: { de: 'Kapazität', en: 'Capacity' },
  tabFlags: { de: 'Konflikt-Flags', en: 'Conflict flags' },
  tabImport: { de: 'Import', en: 'Import' },

  descCatalogue: {
    de: 'Was der Betrieb anbaut: Kategorien, die Kulturen darin und ihre Sorten — eine Hierarchie, ein Baum. Hier steht die IDENTITÄT einer Kultur (Code, Name, Anbaumodell, Einordnung), nicht ihre Agronomie: „Rezept“ öffnet die Profilversion, der diese Werte gehören. Die Kategorie einer Kultur bestimmt, wo sie in Planung und Auswertung erscheint, und welche Linienvorgaben eine neue Kultur kopieren kann.',
    en: 'What the farm grows: categories, the crops in them, and their varieties — one hierarchy, one tree. This page owns a crop’s IDENTITY (code, name, cultivation model, where it sits), not its agronomy: “Recipe” opens the profile version that owns those values. A crop’s category decides where it appears across planning and evaluation, and which line defaults a new crop can copy.',
  },
  // R-219: the old line said what a profile IS but never what it is NOT, so the
  // recipe shown further down this screen read as something you could edit here.
  // Naming the boundary is the whole point: stage plan + bands + version live
  // here, the recipe lives on the crop.
  descProfiles: {
    de: 'Versionierte Anbauprofile — der VOLLSTÄNDIGE Datensatz, nach dem eine Kultur gefahren und beurteilt wird: Phasenplan (welcher Tag welche Phase), Rezept (Licht, Spektrum, Luftstrom, Bewässerung) und die 5-Band-Alarmgrenzen. Nur eine AKTIVE Version steuert einen Raum; die Aktivierung ist die gärtnerische Freigabe. Alle drei sind an die Version gebunden: eine laufende Charge behält bis zur Ernte das, womit sie ausgesät wurde.',
    en: 'Versioned crop profiles — the COMPLETE record a crop is grown and judged by: the stage plan (which day is which stage), the recipe (light, spectrum, airflow, irrigation) and the 5-band alarm limits. Only an ACTIVE version governs a room, and activating one IS the horticulture approval. All three are pinned to the version: a growing batch keeps what it was sown under until harvest.',
  },
  descCapacity: {
    de: 'Wie viel eine Kultur physisch belegt — nutzbare Ebenen je Rack, Tabletts je Ebene, Pflanzplätze je Tablett. Daraus rechnet der Belegungsplaner, wie viel ein Raum wirklich fasst: dasselbe Rack fasst 13 Microgreen- oder 8 Transplant-Tabletts, deshalb ist das eine Eigenschaft der Kultur und keine Raumkonstante. Eine Angabe wird von der spezifischsten Regel übernommen; was NIRGENDS steht, ist ein Fehler und kein Standardwert — eine Kapazität, die niemand konfiguriert hat, darf keine Lieferzusage werden.',
    en: 'How much space a crop physically takes — usable levels per rack, trays per level, planting sites per tray. The scheduler turns this into how much a room really holds: the same rack is 13 microgreen trays or 8 transplanted ones, which is why this belongs to the crop and is not a room constant. A value is taken from the most specific spec that states it; one that NOTHING states is an error rather than a default, because a capacity nobody configured must never become a delivery commitment.',
  },
  // R-222: this stopped being the place the work happens. The sign-off is on the
  // flagged BAND inside the profile now, so this page must say what it IS — the
  // full audit list — instead of claiming to be the step.
  descFlags: {
    de: 'Das vollständige Verzeichnis aller Stellen, an denen die Quelldokumente sich widersprachen oder schwiegen, samt getroffener Entscheidung und Freigabe. Ein ungezeichnetes Flag blockiert die Aktivierung jedes Profils, dessen Bänder es tragen. Freigegeben wird direkt am betroffenen BAND im Profil, wo der Wert sichtbar ist — diese Seite ist der Prüfnachweis, kein Arbeitsschritt.',
    en: 'The full register of every place the source documents contradicted each other or were silent, the call taken, and who signed it. An unsigned flag blocks activating any profile whose bands carry it. Sign-off happens on the flagged BAND itself inside the profile, where the value is visible — this page is the audit record, not a step in the work.',
  },
  descImport: {
    de: 'Lädt die normalisierte Klimamatrix. Der Import legt Profile als ENTWÜRFE an und meldet Konflikte, wo die Quelle sich widerspricht — er aktiviert nichts und rührt keine laufende Version an. Ein erneuter Import derselben Kultur erzeugt eine neue Version, überschreibt also nie, was bereits einen Raum steuert.',
    en: 'Loads the normalised climate matrix. The import creates profiles as DRAFTS and raises flags where the source contradicts itself — it activates nothing and touches no running version. Re-importing a crop adds a new version rather than overwriting the one already governing a room.',
  },

  // ── the catalogue tree ─────────────────────────────────────────────────────
  catalogueHint: {
    de: 'Kategorie → Kultur → Sorte, beliebig tief. Jede Ebene wird dort bearbeitet, wo sie steht: "Unterkategorie" legt direkt darunter an, "Sorte hier" hängt an genau diese Kultur.',
    en: 'Category → crop → variety, at any depth. Each level is edited where it sits: “Add subcategory” creates one directly beneath, “Add variety” hangs one on that exact crop.',
  },
  addSubcategory: { de: 'Unterkategorie', en: 'Add subcategory' },
  addPlantHere: { de: 'Kultur hier', en: 'Add crop here' },
  addPlantType: { de: 'Kulturart hinzufügen', en: 'Add a plant type' },
  addVarietyHere: { de: 'Sorte hier', en: 'Add variety' },
  createPlantType: { de: 'Kulturart anlegen', en: 'Create plant type' },
  cropCount: { de: '{n} Kulturen', en: '{n} crops' },
  cropCountOne: { de: '1 Kultur', en: '1 crop' },
  varietyCount: { de: '{n} Sorten', en: '{n} varieties' },
  varietyCountOne: { de: '1 Sorte', en: '1 variety' },
  emptyBranch: { de: 'leer', en: 'empty' },
  expand: { de: 'Aufklappen', en: 'Expand' },
  collapse: { de: 'Zuklappen', en: 'Collapse' },
  filterCatalogue: { de: 'Katalog durchsuchen', en: 'Search the catalogue' },
  filterCataloguePlaceholder: { de: 'Code, Name oder Sorte', en: 'Code, name or variety' },
  noCropMatch: { de: 'Keine Kultur passt zu „{q}“.', en: 'No crop matches “{q}”.' },

  // ── crops and varieties, edited in the tree (R-210) ────────────────────────
  editCrop: { de: 'Kultur bearbeiten', en: 'Edit crop' },
  editVariety: { de: 'Sorte bearbeiten', en: 'Edit variety' },
  newVariety: { de: 'Neue Sorte', en: 'New variety' },
  colName: { de: 'Name', en: 'Name' },
  sortBy: { de: 'Sortieren nach', en: 'Sort by' },
  sortByName: { de: 'Name (A–Z)', en: 'Name (A–Z)' },
  sortByRecent: { de: 'Zuletzt geändert', en: 'Recently updated' },
  pickModel: { de: '— Anbaumodell wählen —', en: '— choose a cultivation model —' },
  modelLockedHasRecipe: {
    de: 'Nach dem ersten Rezept fest. Das Modell bestimmt den Phasenapparat, an dem die Rezepte dieser Kultur hängen — ein Wechsel würde sie mit ihrer eigenen Kultur uneins machen. Für eine andere Anbauart eine NEUE Kultur anlegen.',
    en: 'Fixed once the crop has a recipe. The model decides the stage machine its recipes hang off, so changing it would leave them disagreeing with their own crop. Create a NEW crop for a different growing method.',
  },
  colSupplier: { de: 'Saatgut-Lieferant', en: 'Seed supplier' },

  // R-210: the manuals' per-variety figures. The heading and note are load-bearing
  // — they are what stops an editable number reading as a scheduler input (§15).
  manualFigures: { de: 'Angaben aus dem Handbuch', en: 'Figures from the manual' },
  manualFiguresNote: {
    de: 'Was das Produktionshandbuch für diese Sorte angibt — Referenzwerte, freigabepflichtig (C-18). Sie steuern derzeit nichts: die Pflanzdichte für die Planung wird unter Kapazität gesetzt. Leer lassen, wo das Handbuch nichts sagt — eine Lücke ist ehrlicher als eine Schätzung.',
    en: 'What the production manual states for this variety — reference values, pending sign-off (C-18). They drive nothing today: the planting density used for scheduling is set under Capacity. Leave blank where the manual is silent — a gap is more honest than a guess.',
  },
  colPlantsPerTray: { de: 'Pflanzen je Tray', en: 'Plants per tray' },
  colLifecycleDays: { de: 'Lebenszyklus (Tage)', en: 'Lifecycle (days)' },
  colFirstCutDay: { de: 'Erster Schnitt (Tag)', en: 'First cut (day)' },
  colMaxCuts: { de: 'Schnitte max.', en: 'Max cuts' },
  colRegrowthDays: { de: 'Nachwuchs (Tage)', en: 'Regrowth (days)' },
  // `colSubstrate` is defined once, further down with the capacity columns — the
  // same word for the same thing, so it is not restated here.
  colYieldTier: { de: 'Ertragsklasse', en: 'Yield tier' },
  blackout: { de: 'Dunkelheit', en: 'blackout' },

  // ── R-213: the agronomy the climate matrix never carried ──────────────────
  agronomy: { de: 'Agronomie', en: 'Agronomy' },
  agronomyFor: { de: 'Agronomie — {stage}', en: 'Agronomy — {stage}' },
  // R-214: the level editor.
  parameters: { de: 'Parameter', en: 'Parameters' },
  parametersFor: { de: 'Parameter — {name}', en: 'Parameters — {name}' },
  parametersNote: {
    de: 'Dieses Rezept gehört ALLEIN dieser Kultur — nichts hier wird von einer Kategorie geerbt, und nichts hier wirkt auf eine andere Kultur. Leer heißt „nicht angegeben": eine Lücke ist ehrlicher als eine plausible Zahl.',
    en: 'This recipe belongs to THIS CROP alone — nothing here is inherited from a category, and nothing here reaches another crop. Blank means “not stated”: a gap is more honest than a plausible number.',
  },
  colStageQualifier: { de: 'Nur für Phase', en: 'Only for stage' },
  stageQualifierHint: {
    de: 'Leer = das Basis-Rezept der Kultur. Ein Phasencode beschreibt NUR, was in dieser einen Phase anders ist; alles andere folgt dem Basis-Rezept. Beides gehört dieser Kultur.',
    en: 'Blank = the crop\u2019s base recipe. A stage code states ONLY what differs in that one stage; everything else follows the base recipe. Both belong to this crop.',
  },
  everyStage: { de: 'alle Phasen', en: 'every stage' },
  inheritValue: { de: 'Nicht angegeben', en: 'Not stated' },
  regimeNotStatedHint: {
    de: '„Nicht angegeben" verhält sich stromabwärts wie „Keine" — es hält aber fest, dass kein Dokument etwas sagt, was „Keine" nicht tut.',
    en: '“Not stated” behaves the same as “None” downstream — but it records that no document says, which “None” does not.',
  },
  // ── R-216: the crop owns its recipe; a line holds copy-on-create defaults ──
  // ── R-216: the recipe editor ────────────────────────────────────────────────
  colAirflowMin: { de: 'Luftbewegung min (m/s)', en: 'Canopy airflow min (m/s)' },
  colAirflowMax: { de: 'Luftbewegung max (m/s)', en: 'Canopy airflow max (m/s)' },
  colWhyThisRow: { de: 'Warum diese Zeile existiert', en: 'Why this row exists' },
  whyThisRowHint: {
    de: 'Für den nächsten Menschen: woher diese Werte stammen. Beim Kopieren aus Linien-Vorgaben wird hier automatisch vermerkt.',
    en: 'For the next person: where these values came from. Copying line defaults stamps this automatically.',
  },
  ecPhInProse: {
    de: 'Hier steht eine EC-/pH-Zahl. Dieser Text wird von nichts ausgewertet; bewertet werden die Schwellenbänder — beide können sich widersprechen.',
    en: 'This states an EC/pH number. Nothing parses this text; the judged targets are the threshold bands, and the two can disagree.',
  },
  bandSaysInstead: { de: 'Das {p}-Band sagt: {v}', en: 'The {p} band says: {v}' },
  recipeProblems: {
    de: '{n} Probleme — Speichern erst nach Korrektur.',
    en: '{n} problems — fix them to save.',
  },
  toastLineDefaultsSaved: {
    de: 'Linien-Vorgaben gespeichert. Keine bestehende Kultur wurde geändert.',
    en: 'Line defaults saved. No existing crop was changed.',
  },
  clearLineDefaults: { de: 'Vorgaben entfernen', en: 'Remove defaults' },
  clearLineDefaultsTitle: { de: 'Vorgaben dieser Linie entfernen?', en: 'Remove this line\u2019s defaults?' },
  clearLineDefaultsMsg: {
    de: 'Keine bestehende Kultur ändert sich — Vorgaben werden nur in NEUE Kulturen kopiert. Danach beginnt eine neue Kultur dieser Linie leer.',
    en: 'No existing crop changes — defaults are only copied into NEW crops. Afterwards a new crop in this line starts blank.',
  },

  // Validation messages. The pure module returns CODES; these are the words, so
  // `recipe.ts` stays testable without a translation harness.
  err_clockFormat: {
    de: 'Uhrzeit als HH:MM angeben (00:00–23:59).',
    en: 'State the time as HH:MM (00:00\u201323:59).',
  },
  err_spectrumUnpaired: {
    de: 'Beide Grenzen oder keine — halbes Spektrum ist kein Rezept.',
    en: 'Both bounds or neither — half a spectrum is not a recipe.',
  },
  err_spectrumOrder: {
    de: 'Die untere Grenze muss unter der oberen liegen.',
    en: 'The lower bound must be below the upper.',
  },
  err_spectrumRange: { de: '100–1000 nm.', en: '100\u20131000 nm.' },
  err_airflowNegative: { de: 'Nicht negativ.', en: 'Not negative.' },
  err_airflowOrder: { de: 'Min darf nicht über Max liegen.', en: 'Min cannot exceed max.' },
  err_flushMin: { de: 'Mindestens 1 Tag.', en: 'At least 1 day.' },
  err_notANumber: { de: 'Keine Zahl.', en: 'Not a number.' },

  recipe: { de: 'Rezept', en: 'Recipe' },
  recipeFor: { de: 'Rezept — {name}', en: 'Recipe — {name}' },
  setUpRecipe: { de: 'Rezept anlegen', en: 'Set up recipe' },
  setUpRecipeFor: { de: 'Rezept anlegen — {name}', en: 'Set up recipe — {name}' },
  startRecipeNote: {
    de: 'Wie soll das Rezept beginnen? Danach besitzt DIESE PROFILVERSION die Werte; späteren Änderungen der Linien-Vorgaben folgt sie nicht, und andere Versionen derselben Kultur bleiben unberührt.',
    en: 'How should the recipe start? Afterwards THIS PROFILE VERSION owns the values; later changes to the line defaults will not reach it, and other versions of the same crop are untouched.',
  },
  startFromLine: { de: 'Mit den Vorgaben von {name} beginnen', en: 'Start from {name} line defaults' },
  startBlank: { de: 'Leer beginnen', en: 'Start blank' },
  startBlankNote: {
    de: 'Nur eintragen, was das Handbuch nennt. Für manche Kulturen sagt keine Quelle etwas — dann ist eine Lücke ehrlicher als eine plausible Zahl.',
    en: 'State only what the manual says. For some crops no source states anything — a gap is then more honest than a plausible number.',
  },
  noLineDefaultsToCopy: {
    de: 'Keine Linie über dieser Kultur hat Vorgaben, also gibt es nichts zu kopieren.',
    en: 'No line above this crop has defaults, so there is nothing to copy.',
  },
  noRecipeConsequence: {
    de: 'kein Rezept — es gilt die Licht-Einschaltzeit des Raums, und es wird nichts zeitgesteuert bewässert',
    en: 'no recipe — the room\u2019s own lights-on time is used and nothing is scheduled to water',
  },
  stageRulesBadge: { de: '+{n} Phasenregeln', en: '+{n} stage rules' },
  lineDefaults: { de: 'Linien-Vorgaben', en: 'Line defaults' },
  lineDefaultsNone: { de: '(keine)', en: '(none)' },
  lineDefaultsFor: { de: 'Linien-Vorgaben — {name}', en: 'Line defaults — {name}' },
  lineDefaultsNote: {
    de: 'Ein Startpunkt, der in eine NEUE Kultur kopiert wird. Änderungen hier ändern keine der {n} Kulturen, die diese Linie schon enthält — jede besitzt ihr eigenes Rezept.',
    en: 'A starting point copied into a NEW crop. Changing it changes none of the {n} crops already in this line — each owns its own recipe.',
  },
  lineDefaultsEmpty: {
    de: 'Diese Linie hat keine Vorgaben. Eine neue Kultur beginnt hier leer.',
    en: 'This line has no defaults. A new crop starts blank here.',
  },
  applyFromCropHint: {
    de: 'Diese Vorgaben werden bei „Rezept anlegen" an der Kultur selbst kopiert — nicht von hier aus, damit keine Kultur mit eigenem Rezept überschrieben wird.',
    en: 'These defaults are copied from the crop\u2019s own “Set up recipe” step, not from here — so a crop that already has its own recipe cannot be overwritten.',
  },
  applyToCrop: { de: 'Auf eine Kultur kopieren', en: 'Copy onto a crop' },
  applyToCropHint: {
    de: 'Kopiert diese Werte in das Rezept EINER Kultur, die sie danach besitzt. Kulturen mit eigenem Rezept werden abgelehnt, damit nichts stillschweigend überschrieben wird.',
    en: 'Copies these values into ONE crop\u2019s recipe, which then owns them. A crop that already has a recipe is refused, so nothing is silently overwritten.',
  },
  toastTemplateApplied: {
    de: '{rows} Zeilen und {cycles} Zyklen kopiert. Diese Profilversion besitzt sie jetzt.',
    en: '{rows} rows and {cycles} cycles copied. This profile version owns them now.',
  },
  legacyVarietyNote: {
    de: 'Aus dem früheren Vererbungsmodell. Rezepte gehören jetzt der Kultur; diese Zeile wird nirgends mehr gepflegt und sollte entfernt werden.',
    en: 'Left over from the inheritance model. Recipes now belong to the crop; this row is edited nowhere and should be cleared.',
  },
  clearParams: { de: 'Rezept löschen', en: 'Delete recipe' },
  clearParamsTitle: { de: 'Rezept dieser Kultur löschen?', en: 'Delete this crop\u2019s recipe?' },
  clearParamsMsg: {
    de: 'Diese Kultur hat danach KEIN Rezept: es gilt die Licht-Einschaltzeit des Raums, und es wird nichts zeitgesteuert bewässert. Die Werte werden nirgends aufbewahrt.',
    en: 'This crop will then have NO recipe: the room\u2019s own lights-on time is used and nothing is scheduled to water. The values are not kept anywhere.',
  },
  toastParamsSaved: { de: 'Parameter gespeichert.', en: 'Parameters saved.' },
  toastParamsCleared: {
    de: 'Parameter entfernt — wieder geerbt.',
    en: 'Parameters cleared — inherited again.',
  },

  agronomyInheritedNote: {
    de: 'Diese Werte gelten für diese Phase, werden aber NICHT hier gespeichert: sie gehören dem Rezept der Kultur. Zum Ändern das Rezept dieser Kultur im Katalog bearbeiten — hier zu tippen würde eine Kopie ändern, die es nicht gibt.',
    en: 'These values govern this stage but are NOT stored here: they belong to the crop\u2019s recipe. Change them on that crop\u2019s recipe in the Catalogue — typing here would edit a copy that does not exist.',
  },
  cycleSummary: {
    de: '{flow} min Flut · {drain} Ablauf · {l} L',
    en: '{flow} min flow · {drain} drain · {l} L',
  },
  noDrain: { de: 'kein Ablauf', en: 'no drain' },
  agronomyNote: {
    de: 'Was das Betriebshandbuch für dieses Stadium vorgibt und die Klimamatrix nicht enthält. Leer lassen, wo das Handbuch schweigt — eine Lücke ist ehrlicher als eine plausible Zahl.',
    en: 'What the operations manual specifies for this stage and the climate matrix does not carry. Leave blank where the manual is silent — a gap is more honest than a plausible number.',
  },
  lightRecipe: { de: 'Lichtrezept', en: 'Light recipe' },
  // ⚠ R-356 — `colLightsOn`, `lightsOnHint`, `colLightsOnUnit`, `colLightsOnHint`
  // and `helpLightsOn` are DELETED. Every one of them ended in the same promise —
  // that without a lights-on clock no plan is issued — and that refusal no longer
  // exists: a stage's lamps follow its blocks, counted in minutes from the moment
  // the crop started. The recipe no longer asks for the clock, so the copy that
  // explained it goes with the field rather than waiting to be rendered again.
  colSpectrumLow: { de: 'Spektrum von (nm)', en: 'Spectrum from (nm)' },
  colSpectrumHigh: { de: 'Spektrum bis (nm)', en: 'Spectrum to (nm)' },
  spectrumHint: {
    de: 'PAR ist 400–700 nm (ANSI/ASABE S640). Beide Werte oder keiner.',
    en: 'PAR is 400–700 nm (ANSI/ASABE S640). Both bounds or neither.',
  },
  colSpectrumNote: { de: 'Spektrum-Hinweis', en: 'Spectrum note' },
  airflow: { de: 'Luftbewegung am Bestand', en: 'Canopy airflow' },
  airflowHint: {
    de: 'm/s. Stehende Luft bei hoher Feuchte begünstigt Botrytis.',
    en: 'm/s. Stagnant air at high humidity invites Botrytis.',
  },
  irrigation: { de: 'Bewässerung', en: 'Irrigation' },
  colIrrigationMode: { de: 'Bewässerungsart', en: 'Irrigation regime' },
  irrigationModeHint: {
    de: 'Bestimmt, was ein Plan ansteuern darf. Nur Flut und Tropf dürfen düngen — in der Keimung wird NIE gedüngt.',
    en: 'Decides what a plan may drive. Only flood and drip may fertigate — germination is NEVER fertigated.',
  },
  irrNone: { de: 'Keine', en: 'None' },
  irrMist: { de: 'Sprühnebel', en: 'Mist' },
  irrPlainWater: { de: 'Klarwasser', en: 'Plain water' },
  irrEbbFlood: { de: 'Ebbe & Flut', en: 'Ebb & flood' },
  irrDrip: { de: 'Tropf', en: 'Drip' },
  colFlushDays: { de: 'Spülung alle (Tage)', en: 'Flush every (days)' },
  colFertigationNote: { de: 'Düngeprogramm', en: 'Fertigation programme' },
  fertigationNoteHint: {
    de: 'EC- und pH-ZIELE stehen bei den Schwellenbändern. Hier steht das Rezept dahinter — welche Nährstoffe wann dazukommen.',
    en: 'EC and pH TARGETS live with the threshold bands. This is the recipe behind them — which nutrients join when.',
  },
  schedule: { de: 'Flutplan', en: 'Flood schedule' },
  noCycles: { de: 'Keine Zyklen erfasst.', en: 'No cycles recorded.' },
  derivedWater: {
    de: '{n} Zyklen/Tag · {l} L je Tray/Tag — errechnet aus den Zeilen, nicht gespeichert.',
    en: '{n} cycles/day · {l} L per tray/day — counted from the rows, not stored.',
  },
  photoperiodIs: { de: '{h} h Licht', en: '{h} h light' },
  // DLI is computed from PPFD and the photoperiod, never stored — the label says
  // so, because a number with no input field invites "where do I change it?".
  dliIs: { de: 'DLI {lo}–{hi} mol/m²/Tag (berechnet)', en: 'DLI {lo}–{hi} mol/m²/day (derived)' },
  notStated: { de: 'nicht angegeben', en: 'not stated' },
  yieldLow: { de: 'Niedrig', en: 'Low' },
  yieldMedium: { de: 'Mittel', en: 'Medium' },
  yieldHigh: { de: 'Hoch', en: 'High' },
  varietyFigures: { de: '{n} Pfl./Tray · {d} T', en: '{n}/tray · {d} d' },
  modelChangeWarning: {
    de: 'Ändert Stadienfolge, Erntelogik und Kapazitätsrechnung — bestehende Profile dieser Kultur passen danach womöglich nicht mehr.',
    en: 'Changes the stage machine, harvest logic and capacity maths — this crop’s existing profiles may no longer fit.',
  },
  deleteCropTitle: { de: 'Kultur {code} löschen?', en: 'Delete crop {code}?' },
  deleteCropMsg: {
    de: 'Nur möglich, solange nichts daran hängt. Profile, Kapazitätsangaben, Chargen oder Zyklen zu dieser Kultur führen zur Ablehnung durch den Server — deaktivieren statt löschen, wenn sie in der Historie steht.',
    en: 'Only possible while nothing references it. Profiles, capacity specs, batches or cycles on this crop will make the server refuse — deactivate rather than delete if it appears in history.',
  },
  deleteVarietyTitle: { de: 'Sorte {code} löschen?', en: 'Delete variety {code}?' },
  deleteVarietyMsg: {
    de: 'Nur möglich, solange keine Charge und kein Profil auf diese Sorte verweist.',
    en: 'Only possible while no batch or profile refers to this variety.',
  },

  // ── add a plant type, in one pass ──────────────────────────────────────────
  colCategory: { de: 'Kategorie', en: 'Category' },
  colLatinName: { de: 'Botanischer Name', en: 'Botanical name' },
  colUsableLayers: { de: 'Nutzbare Ebenen', en: 'Usable layers' },
  colLidType: { de: 'Deckeltyp', en: 'Lid type' },
  latinNameHint: {
    de: 'Leer lassen, wenn die Botanik unklar ist — eine plausible Vermutung ist schlechter als eine Lücke.',
    en: 'Leave empty when the botany is unclear — a plausible guess is worse than a blank.',
  },
  modelDrivesEverything: {
    de: 'Das Anbaumodell — nicht die Kategorie — bestimmt Stadienfolge, Erntelogik und Kapazitätsrechnung.',
    en: 'The cultivation model — not the category — drives the stage machine, harvest logic and capacity maths.',
  },
  stepCapacity: { de: 'Pflanzdichte', en: 'Planting density' },
  stepCapacityNote: {
    de: 'Ohne eigene Angabe gilt der Standard des Anbaumodells. Passt der nicht, wird die Kultur falsch eingeplant — der Resolver errät nichts, sondern meldet einen Fehler.',
    en: 'Without its own figure the cultivation model default applies. If that is wrong the crop is scheduled wrong — the resolver never guesses, it errors.',
  },
  withCapacity: { de: 'Eigene Dichte festlegen', en: 'Set its own density' },
  stepProfile: { de: 'Kulturprofil', en: 'Crop profile' },
  stepProfileNote: {
    de: 'Legt einen Entwurf an, in dem anschließend Stadienplan und Schwellenbänder gepflegt werden. Ohne Profil hat die Kultur keine Grenzwerte.',
    en: 'Creates a draft to fill in with a stage plan and threshold bands. Without a profile the crop has no limits at all.',
  },
  withProfile: { de: 'Profilentwurf anlegen', en: 'Create a draft profile' },
  capacityFromPlantFlow: {
    de: 'Beim Anlegen der Kulturart erfasst.',
    en: 'Captured when the plant type was created.',
  },
  profileFromPlantFlow: {
    de: 'Entwurf beim Anlegen der Kulturart erzeugt — Stadienplan und Bänder fehlen noch.',
    en: 'Draft created with the plant type — stage plan and bands still to be filled in.',
  },
  toastPlantCreated: { de: 'Kulturart {code} angelegt', en: 'Plant type {code} created' },

  // ── manual-derived import (C-8) ────────────────────────────────────────────
  manualImportTitle: {
    de: 'Profile aus den Produktionshandbüchern',
    en: 'Profiles from the production manuals',
  },
  manualImportHint: {
    de: 'Die 15 Nicht-Microgreen-Kulturen haben keine Tabelle. Ihre Profile stammen aus den Handbüchern und liegen der Anwendung bei — ein Klick genügt, ein Upload ist nur für eine korrigierte Fassung nötig. Die Bänder werden aus Sollbereich und Alarmschwelle abgeleitet (C-22); die Profile gelten als unvollständig und brauchen die Freigabe der Gartenbau-Leitung.',
    en: 'The 15 non-microgreen crops have no spreadsheet. Their profiles come from the manuals and ship with the build — one click is enough, and an upload is only for a corrected file. Bands are derived from the optimal range and the alert threshold (C-22); the profiles are marked incomplete and need horticulture sign-off.',
  },
  runManualImport: { de: 'Mitgelieferte Datei importieren', en: 'Import the shipped file' },
  manualImportUpload: { de: 'oder eine korrigierte CSV hochladen', en: 'or upload a corrected CSV' },
  reportSource: { de: 'Quelle', en: 'Source' },
  reportShippedFile: { de: 'mitgelieferte Datei', en: 'the shipped file' },
  reportUploadedFile: { de: 'hochgeladene Datei', en: 'an uploaded file' },
  reportProvisional: { de: 'Vorläufige Stadiengrenzen', en: 'Provisional stage boundaries' },
  reportProvisionalHint: {
    de: 'Für diese Kulturen nennt das Handbuch keine Stadiendauern. Die Tagesgrenzen sind gesetzt, damit der Plan aufgeht — sie müssen vor der Aktivierung ersetzt werden (C-25).',
    en: 'For these crops the manual states no stage durations. The day boundaries exist so the plan tiles — they must be replaced before activation (C-25).',
  },

  // Cultivation models
  modelMicrogreen: { de: 'Microgreen', en: 'Microgreen' },
  modelTransplantedSingle: { de: 'Verpflanzt, einmalige Ernte', en: 'Transplanted, single harvest' },
  modelTransplantedMultiCut: { de: 'Verpflanzt, Mehrfachschnitt', en: 'Transplanted, multi-cut' },
  modelContinuousHarvest: { de: 'Laufende Ernte', en: 'Continuous harvest' },
  modelPerennialFruiting: { de: 'Mehrjährig fruchtend', en: 'Perennial fruiting' },

  // Parameters
  paramTemperature: { de: 'Temperatur', en: 'Temperature' },
  paramHumidity: { de: 'Luftfeuchte', en: 'Humidity' },
  paramCo2: { de: 'CO₂', en: 'CO₂' },
  paramVpd: { de: 'VPD', en: 'VPD' },
  paramEc: { de: 'EC', en: 'EC' },
  paramPh: { de: 'pH', en: 'pH' },
  paramNutrientTemp: { de: 'Nährlösungstemperatur', en: 'Nutrient temperature' },
  paramPpfd: { de: 'PPFD', en: 'PPFD' },

  // Taxonomy tab
  addCategory: { de: 'Kategorie hinzufügen', en: 'Add category' },
  editCategory: { de: 'Kategorie bearbeiten', en: 'Edit category' },
  newCategory: { de: 'Neue Kategorie', en: 'New category' },
  // ⚠ `colCode`, `colNameEn`, `colNameDe` and `nameDeFallback` are GONE with the
  // fields they labelled: the catalogue is English-only and its codes are derived
  // from names, never typed. `colName` is the one name label now.
  colParent: { de: 'Übergeordnet', en: 'Parent' },
  colSort: { de: 'Reihenfolge', en: 'Sort' },
  colActive: { de: 'Aktiv', en: 'Active' },
  inactive: { de: 'Inaktiv', en: 'Inactive' },
  colNotes: { de: 'Notizen', en: 'Notes' },
  noParent: { de: '— keine —', en: '— none —' },
  deleteCategoryTitle: { de: 'Kategorie {code} löschen?', en: 'Delete category {code}?' },
  deleteCategoryMsg: {
    de: 'Kategorien, die Kulturen oder Unterkategorien enthalten, können nicht gelöscht werden.',
    en: 'A category that still holds crops or subcategories cannot be deleted.',
  },

  // Profiles tab
  colCrop: { de: 'Kultur', en: 'Crop' },
  colCropCultivar: { de: 'Kultur / Sorte', en: 'Crop / Cultivar' },
  colModel: { de: 'Anbaumodell', en: 'Cultivation model' },
  colVersion: { de: 'Version', en: 'Version' },
  colStatus: { de: 'Status', en: 'Status' },
  // No unit in the header: the value carries its own (R-356) — `14 d`, `36 h`.
  colCycle: { de: 'Zyklus', en: 'Cycle' },
  colSource: { de: 'Quelle', en: 'Source' },
  colCompleteness: { de: 'Vollständigkeit', en: 'Completeness' },
  statusDraft: { de: 'Entwurf', en: 'Draft' },
  statusActive: { de: 'Aktiv', en: 'Active' },
  statusRetired: { de: 'Archiviert', en: 'Retired' },
  complete: { de: 'Vollständig', en: 'Complete' },
  incomplete: { de: 'Unvollständig', en: 'Incomplete' },
  newProfile: { de: 'Neues Profil', en: 'New profile' },
  // R-217: a draft is no longer an empty shell, and the button has to stop
  // implying one. The day ranges it arrives with are a scaffold to correct.
  newProfileHint: {
    de: 'Der Entwurf startet mit dem Stufensatz des Anbaumodells und einer gleichmäßigen Tagesaufteilung — die Aufteilung ist ein Platzhalter, keine Agronomie. Grenzwerte enthält er keine. Für eine bestehende Kultur ist „Neue Version" fast immer richtig: sie übernimmt Plan und Grenzwerte.',
    en: 'The draft starts with the cultivation model’s stage set and an even day split — that split is a placeholder, not agronomy. It carries no thresholds. For a crop that already has a profile, “New version” is almost always what you want: it carries the plan and the bands over.',
  },
  // D-130. The recipe is NOT version-pinned while the alarm bands are, so a save
  // here reaches a batch that is already growing — on 2026-08-06 one such edit
  // moved 36 crops and reissued a live room's plan mid-cycle. That is the
  // accepted design; leaving it unsaid is what §15 forbids.
  recipeAffectsRunningBatches: {
    de: '⚠ Speichern wirkt sofort — auch auf bereits laufende Chargen dieser Kultur, ab dem nächsten Abgleich. Anders als die Alarmgrenzen ist das Rezept nicht an eine Version gebunden.',
    en: '⚠ Saving takes effect immediately — including on batches of this crop that are already growing, from the next sync. Unlike the alarm limits, the recipe is not pinned to a version.',
  },
  newVersion: { de: 'Neue Version', en: 'New version' },
  // R-250. Shown only where the indent cannot say it on its own — two branches
  // off one parent, or a parent several rows up. Reads "from v1", not "derived
  // from profile <uuid>": the version number is what an agronomist has in mind.
  derivedFrom: { de: '(aus v{version})', en: '(from v{version})' },
  // R-311 — ordering the Profiles list once it grows past a screenful. There is no
  // sort CONTROL: the columns are clickable, like every other table here.
  colUpdated: { de: 'Geändert', en: 'Updated' },
  colActions: { de: 'Aktionen', en: 'Actions' },
  // The Profiles tab searches rather than picks (2026-08-26). Reuses the
  // catalogue's placeholder — same matcher, so the same hint is the honest one.
  filterProfiles: { de: 'Profile durchsuchen', en: 'Search the profiles' },
  // 2026-08-26 — the Base/override rule, stated on the page. Previously the only
  // way to learn it was to notice the second table was all dashes and work out why.
  recipeBaseTitle: { de: 'Basis — gilt für jede Phase', en: 'Base — applies to every stage' },
  recipeBaseNote: {
    de: 'Genau EINE Basiszeile pro Version, gültig für alle Phasen. Ohne sie kann die Version nicht aktiviert werden.',
    en: 'Exactly ONE base row per version, and it applies to every stage. A version without one cannot be activated.',
  },
  recipeOverridesTitle: { de: 'Phasen-Abweichungen ({n})', en: 'Stage overrides ({n})' },
  recipeOverridesNote: {
    de: 'Jede Zeile nennt nur, was DIESE Phase ändert; ein Strich (–) bedeutet: Wert kommt aus der Basis. Höchstens eine Zeile pro Phase. Zusammengehörige Felder wechseln gemeinsam — Spektrum als Paar, Luftstrom als Paar, und das Bewässerungsregime bringt seinen ganzen Zyklusplan mit.',
    en: 'Each row states only what THAT stage changes; a dash (–) means the value comes from Base. At most one row per stage. Related fields move together — spectrum as a pair, airflow as a pair, and the irrigation regime brings its whole cycle schedule with it.',
  },
  createdOn: { de: 'Angelegt {date}', en: 'Created {date}' },
  // Said out loud rather than letting the indentation quietly vanish: a column
  // sort moves a version away from its parent, so the tree cannot survive it.
  sortFlattensTree: {
    de: 'Sortiert nach Spalte — flache Liste. Der Ableitungsbaum erscheint, wenn keine Spalte sortiert ist (Spaltenkopf erneut klicken). Die Herkunft steht weiter an der Version.',
    en: 'Sorted by column — a flat list. The derivation tree returns when no column is sorted (click the header again). Each row still says which version it came from.',
  },
  // R-219: three states because they need three different actions — nothing to
  // do, needs sign-off, needs a profile at all.
  profileActiveBadge: { de: 'Profil v{v} aktiv', en: 'Profile v{v} active' },
  // R-221. The words are chosen to separate "this is happening now" from "this
  // is waiting": an active profile on unsigned data is judging a real room
  // against a number nobody approved, a draft is merely blocked from doing so.
  governingUnsigned: {
    de: '⚠ steuert mit ungezeichnet {codes}',
    en: '⚠ governing on unsigned {codes}',
  },
  blockedUnsigned: { de: 'blockiert durch {codes}', en: 'blocked by {codes}' },
  colImpact: { de: 'Betrifft', en: 'Affects' },
  flagGoverningNow: {
    de: '⚠ steuert {n} laufend',
    en: '⚠ governing {n} now',
  },
  flagBlocksCrops: { de: '{n} Kulturen betroffen', en: '{n} crops affected' },
  // R-222. Said BEFORE the button is pressed: signing accepts numbers a live
  // room is already being judged against, not just paperwork.
  flagImpactLive: {
    de: '⚠ {active} aktive Profile steuern JETZT reale Räume mit diesen Werten ({crops} Kulturen betroffen). Freigeben akzeptiert sie und hebt die blockierten Aktivierungen auf.',
    en: '⚠ {active} active profiles are governing real rooms with these values RIGHT NOW ({crops} crops affected). Signing off accepts them and releases the blocked activations.',
  },
  flagImpactBlocked: {
    de: 'Blockiert derzeit die Aktivierung für {crops} Kulturen. Nichts steuert damit einen Raum.',
    en: 'Currently blocking activation for {crops} crops. Nothing is governing a room with these values.',
  },
  alreadySignedOff: {
    de: 'Diese Entscheidung ist freigegeben — die Werte gelten als akzeptiert.',
    en: 'This adjudication is signed off — the values count as accepted.',
  },
  signoffNeedsApprove: {
    de: 'Zum Freigeben ist die Berechtigung „Approve" im Modul Farming nötig. Freigeben akzeptiert Werte, die von der Quelle abweichen — dieselbe Befugnis wie ein Profil in Betrieb zu nehmen.',
    en: 'Signing off needs Approve on the farming module. It accepts values that disagree with their source document — the same authority as putting a profile in charge of a room.',
  },
  profileDraftBadge: { de: 'Profil v{v} Entwurf', en: 'Profile v{v} draft' },
  profileNoneBadge: { de: 'Kein Profil', en: 'No profile' },
  newVersionTitle: {
    de: 'Neue Version von {code} v{version} anlegen?',
    en: 'New version from {code} v{version}?',
  },
  newVersionMsg: {
    de: 'Der neue Entwurf übernimmt Stufenplan und alle Grenzwerte dieser Version. Nichts wird aktiviert, und diese Version bleibt unverändert — laufende Chargen bleiben auf ihrer Version.',
    en: 'The new draft copies this version’s stage plan and every threshold band. Nothing is activated and this version is left untouched — growing batches stay on the version they were sown under.',
  },
  allCrops: { de: 'Alle Kulturen', en: 'All crops' },
  filterCrop: { de: 'Kultur filtern', en: 'Filter by crop' },
  activate: { de: 'Aktivieren', en: 'Activate' },
  activateTitle: { de: 'Profil {code} v{version} aktivieren?', en: 'Activate profile {code} v{version}?' },
  activateMsg: {
    de: 'Damit steuert dieses Bandset reale Räume, und Sie werden als Freigeber eingetragen. Die bisher aktive Version wird archiviert.',
    en: 'This band set will then govern real rooms and you are recorded as the approver. The previously active version is retired.',
  },
  profileActivated: { de: 'Profil aktiviert', en: 'Profile activated' },

  // Draft only. The button does not exist on an active or retired version, so
  // these strings never have to explain a refusal the operator could hit.
  deleteProfile: { de: 'Löschen', en: 'Delete' },
  deleteProfileTitle: {
    de: 'Entwurf {code} v{version} löschen?',
    en: 'Delete draft {code} v{version}?',
  },
  deleteProfileMsg: {
    de: 'Der Stufenplan und alle Grenzwerte dieses Entwurfs werden mitgelöscht. Das lässt sich nicht rückgängig machen.',
    en: "This draft's stage plan and every threshold band go with it. This cannot be undone.",
  },
  deleteProfileConfirm: { de: 'Entwurf löschen', en: 'Delete draft' },
  profileDeleted: { de: 'Entwurf gelöscht', en: 'Draft deleted' },
  // R-176: activating mid-cycle is SAFE and intentional — growing batches keep
  // the version they were sown under. The operator still has to be told, or the
  // console says v2 governs a room whose crop is judged against v1.
  activateStayBehind_one: {
    de: '⚠ 1 laufende Charge wird weiterhin nach einer älteren Version beurteilt, bis sie geerntet ist. Die neue Version gilt nur für NEUE Buchungen.',
    en: '⚠ 1 growing batch stays on an older version until it is harvested. The new version governs NEW bookings only.',
  },
  activateStayBehind_many: {
    de: '⚠ {count} laufende Chargen werden weiterhin nach älteren Versionen beurteilt, bis sie geerntet sind. Die neue Version gilt nur für NEUE Buchungen.',
    en: '⚠ {count} growing batches stay on older versions until they are harvested. The new version governs NEW bookings only.',
  },
  // R-224: the recipe is version-pinned now, so a running batch keeps its lights,
  // spectrum, airflow and irrigation as well as its alarm limits. That is the
  // protection AND the cost, so the correction path is offered explicitly here —
  // default NO, and Cancel is the safe answer.
  rebaseTitle: {
    de: 'Laufende Chargen ebenfalls auf v{version} umstellen?',
    en: 'Move growing crops onto v{version} too?',
  },
  rebaseMsg_one: {
    de: '1 Charge wächst derzeit nach einer älteren Version. Umstellen bedeutet: Licht, Spektrum, Luftstrom, Bewässerung UND Alarmgrenzen dieser Version gelten ab der nächsten Synchronisierung für sie. Abbrechen lässt sie auf ihrer bisherigen Version — das ist die sichere Antwort.',
    en: '1 crop is growing under an older version. Moving it means this version’s light, spectrum, airflow, irrigation AND alarm limits apply to it from the next sync. Cancel leaves it where it is — that is the safe answer.',
  },
  rebaseMsg_many: {
    de: '{count} Chargen wachsen derzeit nach älteren Versionen. Umstellen bedeutet: Licht, Spektrum, Luftstrom, Bewässerung UND Alarmgrenzen dieser Version gelten ab der nächsten Synchronisierung für sie. Abbrechen lässt sie auf ihren bisherigen Versionen — das ist die sichere Antwort.',
    en: '{count} crops are growing under older versions. Moving them means this version’s light, spectrum, airflow, irrigation AND alarm limits apply to them from the next sync. Cancel leaves them where they are — that is the safe answer.',
  },
  rebaseConfirm: { de: 'Ja, umstellen', en: 'Yes, move them' },
  rebased_one: {
    de: '1 laufende Charge auf diese Version umgestellt',
    en: '1 growing crop moved onto this version',
  },
  rebased_many: {
    de: '{count} laufende Chargen auf diese Version umgestellt',
    en: '{count} growing crops moved onto this version',
  },
  tabRecipe: { de: 'Rezept', en: 'Recipe' },
  recipeTabHint: {
    de: 'Licht, Spektrum, Luftstrom und Bewässerung dieser Version. Wie der Phasenplan und die Alarmgrenzen ist das Rezept an die Version gebunden: eine laufende Charge behält das, womit sie ausgesät wurde.',
    en: 'This version’s light, spectrum, airflow and irrigation. Like the stage plan and the alarm limits, the recipe belongs to the version: a growing batch keeps what it was sown under.',
  },
  // R-224 per-field help. These are DEFINITIONS — what the field means and what
  // blank means — not instructions on how to type into it. Anything about format
  // stays in `hint`, which is always visible; a definition an operator needs once
  // does not belong in permanent grey text.
  helpSpectrum: {
    de: 'PAR-Bereich in Nanometern, den die Leuchten liefern sollen (ANSI/ASABE S640 definiert PAR als 400–700 nm). Beide Grenzen gehören zusammen — eine allein ist kein Spektrum.',
    en: 'The PAR band the lamps should deliver, in nanometres (ANSI/ASABE S640 defines PAR as 400–700 nm). The two bounds are one fact — one without the other is not a spectrum.',
  },
  helpAirflow: {
    de: 'Luftgeschwindigkeit über dem Bestand, in m/s. Wichtig gegen Pilzdruck: bei Erdbeeren ist es die einzige Botrytis-Kontrolle im Dokument. Leer = kein Handbuch nennt einen Wert.',
    en: 'Air speed over the canopy, in m/s. It is a disease control, not a comfort setting — for strawberry it is the only Botrytis control any manual gives. Blank = no manual states one.',
  },
  helpIrrigationMode: {
    de: 'Wie diese Phase bewässert wird. „none“ ist eine ENTSCHEIDUNG (Erntetag bleibt trocken) und etwas völlig anderes als leer, was heißt: niemand hat es angegeben.',
    en: 'How this stage is watered. “none” is a DECISION (harvest day is deliberately dry) and is not the same as blank, which means nobody has stated it.',
  },
  helpFlush: {
    de: 'Abstand in Tagen zwischen Klarwasser-Spülungen, die Salze aus dem Substrat waschen. Nicht die Bewässerungsfrequenz — die steht in den Zyklen darunter.',
    en: 'Days between plain-water flushes that wash accumulated salts out of the substrate. Not the watering frequency — that is the cycle list below.',
  },
  helpFertigation: {
    de: 'Das Nährstoffprogramm in Worten. EC- und pH-Zielwerte stehen bewusst NICHT hier, sondern in den Alarmgrenzen — sonst gäbe es zwei Quellen, die sich widersprechen können.',
    en: 'The feed programme in words. EC and pH targets deliberately do NOT belong here — they live in the alarm limits, so there is one owner that cannot disagree with itself.',
  },
  helpPhotoperiod: {
    de: 'Beleuchtete Stunden je 24 h in dieser Phase. 0 = Dunkelphase (Corn Shoots wachsen durchgehend blanchiert). Gespeichert wird in Minuten.',
    en: 'Lit hours per 24 h in this stage. 0 = blackout (Corn Shoots are blanched end to end). Stored as minutes.',
  },
  noDeactivateWhy: {
    de: 'Es gibt bewusst kein „Deaktivieren“: eine laufende Version zu entziehen nähme dem Raum die Überwachung ganz — Blindheit ist das schlechtere Versagen als ein Fehlalarm. Eine Version endet, indem eine NEUE aktiviert wird; danach steht sie auf „archiviert“. Eine aktive Version ohne laufende Charge steuert ohnehin nichts.',
    en: 'There is deliberately no “Deactivate”: revoking a governing version would take the room out of monitoring altogether, and blindness is the worse failure of the two. A version ends by a NEW one being activated, which retires it. An active version with no batch running governs nothing anyway.',
  },
  // Per-field definitions for the pages the operator meets least often. Capacity
  // is the jargon-heaviest screen in the console — "sites per tray" and
  // "planting site unit" mean nothing without the tray in front of you.
  helpScope: {
    de: 'Für wen diese Angabe gilt. Aufgelöst wird von der SPEZIFISCHSTEN Regel abwärts: Raum schlägt Sorte, Sorte schlägt Kultur, Kultur schlägt Anbaumodell. Jedes Feld wird einzeln aufgelöst — eine Raumregel, die nur die Ebenenzahl nennt, erbt den Rest.',
    en: 'Who this spec applies to. Resolution runs from the MOST specific down: room beats variety, variety beats crop, crop beats cultivation model. Each field resolves on its own, so a room spec that states only the layer count inherits the rest.',
  },
  helpUsableLayers: {
    de: 'Wie viele Ebenen eines Racks diese Kultur wirklich nutzen kann — nicht wie viele das Rack hat. Ein hoher Bestand lässt Ebenen aus. Der Wert wird auf die real vorhandenen Ebenen des Raums gedeckelt, damit eine Fehlkonfiguration ein Rack nicht überbucht.',
    en: 'How many of a rack’s levels this crop can actually use — not how many the rack has. A tall canopy skips levels. The value is clamped to the room’s real level count, so a mis-configured number cannot overbook a rack.',
  },
  helpTraysPerLayer: {
    de: 'Tabletts nebeneinander auf einer Ebene. Fast überall 1; konfigurierbar, weil halbbreite Tabletts eine normale Variante sind.',
    en: 'Trays side by side on one level. Almost always 1; configurable because half-width trays are a normal variation.',
  },
  helpSitesPerTray: {
    de: 'Pflanzplätze in EINEM Tablett, gezählt in der Einheit daneben: bei „plant“ einzelne Pflanzstellen (18 / 44 / 78 Löcher), bei „subtray“ eingesetzte Schalen (Microgreens: 4). Bei Microgreens sind die einzelnen Pflanzen nicht zählbar — breitwürfig gesät.',
    en: 'Planting positions in ONE tray, counted in the unit beside it: “plant” means individual positions (18 / 44 / 78 holes), “subtray” means nested trays (microgreens: 4). Microgreens have no countable positions — they are sown broadcast.',
  },
  helpPlantsPerRack: {
    de: 'Für Systeme ohne Tabletts — Substratsäcke, Rinnen, Erdbeer-Zeilen. Schließt die Tablett-Kette aus: entweder das eine oder das andere, nie beides.',
    en: 'For non-tray systems — grow bags, channels, strawberry lines. Mutually exclusive with the tray chain: one or the other, never both.',
  },
  helpCapacityPool: {
    de: 'Welchen Raumtyp diese Phase belegt. Eine Anzuchtphase bucht Anzuchtregale, keine Wachstumsfläche — sonst zeigt der Planer Kapazität an, die es nicht gibt. „none“ heißt: belegt gar nichts.',
    en: 'Which kind of room this stage occupies. A nursery stage books nursery shelving, not growing space — otherwise the planner reports capacity that does not exist. “none” means it occupies nothing.',
  },
  // ⚠ R-356 — `colCycleDays`, `helpCycleDays` and `cycleDaysRequired` are DELETED
  // with the input they belonged to. A cycle's length is the sum of its stage
  // plan's blocks and the server derives it on every write; asking an operator to
  // type it as well was asking for a second answer that could disagree.
  reportNoRecipe: { de: 'Entwürfe ohne Rezept', en: 'Drafts with no recipe' },
  reportNoRecipeHint: {
    de: 'Diese Kulturen hatten noch keine Version, aus der ein Rezept übernommen werden konnte — ein Import kennt Phasen und Grenzwerte, nie Licht oder Bewässerung. Die Entwürfe lassen sich erst aktivieren, wenn ein Rezept darauf liegt: Profil öffnen → Rezept → Linien-Vorgaben übernehmen.',
    en: 'These crops had no earlier version to copy a recipe from — an import knows stages and bands, never light or irrigation. Their drafts cannot be activated until one is put on them: open the profile → Recipe → copy the line defaults.',
  },
  baseRow: { de: 'Basis', en: 'Base' },
  colSpectrum: { de: 'Spektrum', en: 'Spectrum' },
  colCycles: { de: 'Zyklen', en: 'Cycles' },
  recipeNeedsDraft: {
    de: 'Die aktuelle Version dieser Kultur ist bereits freigegeben. Ein Rezept kann nur in einen ENTWURF geschrieben werden — legen Sie zuerst über „Neue Version“ einen an.',
    en: 'This crop’s current version is already approved. A recipe can only be written into a DRAFT — create one with “New version” first.',
  },
  activatePreviewFailed: {
    de: 'Konnte nicht ermitteln, welche Chargen auf der alten Version bleiben — Aktivierung abgebrochen.',
    en: 'Could not determine which batches stay on the old version — activation cancelled.',
  },
  governsNewCycles: { de: 'gilt für neue Zyklen', en: 'governs new cycles' },
  stagePlan: { de: 'Phasenplan', en: 'Stage plan' },
  // The two tabs a profile opens in. Named for the QUESTION each answers, not
  // for its table — "Threshold bands" told an operator nothing about what they
  // were looking at, which is what prompted this.
  tabStagePlan: { de: 'Phasenplan', en: 'Stage plan' },
  daysRange: { de: 'Tag {from}–{to}', en: 'day {from}–{to}' },
  tabAlarmLimits: { de: 'Alarmgrenzen', en: 'Alarm limits' },
  profileModalTitle: { de: '{crop} v{version} · {status}', en: '{crop} v{version} · {status}' },
  stagePlanHint: {
    de: 'Welcher Tag zu welcher Phase gehört. Der Plan muss den ganzen Zyklus lückenlos abdecken — daraus weiß die Auswertung, welche Grenzwerte heute gelten.',
    en: 'Which day belongs to which stage. The plan has to cover the whole cycle with no gaps — this is how the evaluator knows which limits apply today.',
  },
  bandsHint: {
    de: 'Was in jeder Phase als zu hoch oder zu niedrig gilt, getrennt für Licht AN und AUS. Hieraus entstehen die Alarme; ohne aktives Profil wird gar nichts beurteilt.',
    en: 'What counts as too high or too low in each stage, split by lights ON and OFF. This is what raises alarms — with no active profile, nothing is judged at all.',
  },
  colStage: { de: 'Phase', en: 'Stage' },
  colDays: { de: 'Tage', en: 'Days' },
  colPhotoperiod: { de: 'Photoperiode', en: 'Photoperiod' },
  // The EDITOR's label carries the unit; the table's does not need to, because
  // every cell there already reads "12 h" or "blackout".
  colPhotoperiodHours: { de: 'Photoperiode (h)', en: 'Photoperiod (h)' },
  photoperiodHint: {
    de: 'Belichtete Stunden je 24 h, 0–24. 0 = Dunkelphase; die „AN"-Bänder gelten dann für volle 24 h. Gespeichert wird in Minuten, halbe Stunden sind erlaubt (16,5).',
    en: 'Lit hours per 24 h, 0–24. 0 = blackout; the “ON” bands then govern the full 24 h. Stored as minutes, so half hours are fine (16.5).',
  },
  // ── R-251: the Models screen ──
  yes: { de: 'Ja', en: 'Yes' },
  no: { de: 'Nein', en: 'No' },
  deleteModelTitle: { de: 'Anbaumodell löschen', en: 'Delete cultivation model' },
  // ⚠ R-315 replaced `tabModels`/`descModels`/`modelsHint`/`addModel`. Cultivation
  // models are gone; this leaf edits the farm's ONE stage vocabulary.
  tabStages: { de: 'Phasen', en: 'Stages' },
  descStages: {
    de: 'Jede Phase, die dieser Betrieb kennt — eine Liste, nicht eine pro Anbaumodell. Ein Rezept darf beliebige davon nennen, in beliebiger Reihenfolge und mehrfach.',
    en: 'Every stage this farm knows — one list, not one per cultivation model. A recipe may name any of them, in any order, and repeat them.',
  },
  stagesHint: {
    de: 'Eine Phase ist ein Name und eine Position. Wo sie stattfindet, wie lange sie läuft und wie hell sie ist, gehört dem REZEPT. Grenzen setzt allein die VERWENDUNG: eine Phase, die ein Rezept schon nutzt, kann nicht entfernt werden — umbenennen geht immer.',
    en: 'A stage is a name and a position. Where it happens, how long it runs and how lit it is belong to the RECIPE. The only limit is USE: a stage a recipe already holds cannot be removed — renaming one always works.',
  },
  editStages: { de: 'Phasen bearbeiten', en: 'Edit stages' },
  toastStagesSaved: { de: 'Phasen gespeichert.', en: 'Stages saved.' },
  stageVocabHint: {
    de: 'Die Reihenfolge ist nur die Anzeigereihenfolge — sie schreibt keinem Rezept etwas vor.',
    en: 'The order here is display order only — it constrains no recipe.',
  },
  // ── the one form: a model is a name + an ordered set of stage NAMES ──
  //
  // ⚠ `colDefaultCycle`, `daysCount`, `colDefaultDays`/`helpDefaultDays`,
  // `colDark`/`helpDark` and `colRepeatable`/`helpRepeatable` were all deleted with
  // the fields they labelled. A model is generic now: which room a stage sits in and
  // how long it runs are the crop RECIPE's business.
  modelStagesSection: { de: 'Phasen', en: 'Stages' },
  colPosition: { de: 'Nr.', en: '#' },
  addStage: { de: 'Phase hinzufügen', en: 'Add stage' },
  moveUp: { de: 'Hoch', en: 'Up' },
  moveDown: { de: 'Runter', en: 'Down' },
  // ⚠ There are deliberately NO code hints. The code is derived from the name and
  // frozen on save; the operator never types one and does not need to read one —
  // `codeWillBe` / `codeIsFixed` / `modelNameHint` were all removed on the user's
  // instruction. The one case where a code matters (a name nothing usable can be
  // built from) is reported in terms of the NAME, below.
  stageNamePlaceholder: { de: 'z. B. Keimung', en: 'e.g. Germination' },
  // What the form refuses, in the operator's words. ⚠ Every message names a NAME,
  // never a code — the operator cannot see or change a code, so pointing at one
  // would name a field that is not on screen.
  needName: { de: 'Das Modell braucht einen Namen.', en: 'The model needs a name.' },
  nameUnusable: {
    de: 'Aus diesem Namen lässt sich kein Code bilden — Buchstaben oder Ziffern verwenden.',
    en: 'No code can be built from this name — use letters or digits.',
  },
  nameTaken: { de: 'Es gibt schon ein Modell namens {name}.', en: 'A model called {name} already exists.' },
  needOneStage: {
    de: 'Der Betrieb braucht mindestens eine Phase.',
    en: 'The farm needs at least one stage.',
  },
  needStageName: { de: 'Jede Phase braucht einen Namen.', en: 'Every stage needs a name.' },
  stageNameUnusable: {
    de: 'Aus einem Phasennamen lässt sich kein Code bilden — Buchstaben oder Ziffern verwenden.',
    en: 'No code can be built from one of the stage names — use letters or digits.',
  },
  stageTwice: { de: 'Die Phase {name} kommt zweimal vor.', en: 'The stage {name} appears twice.' },
  modelSavedStagesNot: {
    de: 'Modell {code} gespeichert, die Phasen NICHT: {error}. Über „Bearbeiten" erneut versuchen.',
    en: 'Model {code} saved, but its stages were NOT: {error}. Reopen it with Edit and try again.',
  },
  modelsHint: {
    de: 'Ein Anbaumodell ist ein Name und eine geordnete Folge von Phasen — mehr nicht. Jedes Modell lässt sich anlegen und bearbeiten, auch die fünf mitgelieferten. Grenzen setzt allein die VERWENDUNG: eine Phase, die ein Rezept schon nutzt, kann nicht entfernt werden, und ein Modell, auf das Kulturen verweisen, nicht gelöscht (dann auf inaktiv setzen).',
    en: 'A cultivation model is a name and an ordered set of stages — that is all. Every model can be created and edited, the five shipped ones included. The only limit is USE: a stage a recipe already holds cannot be removed, and a model crops reference cannot be deleted (set it inactive instead).',
  },
  addModel: { de: 'Modell anlegen', en: 'Add model' },
  editModelTitle: { de: 'Modell {code}', en: 'Model {code}' },
  // ⚠ There is deliberately no "Built-in" badge any more (2026-08-27): every model
  // is editable, so a badge implying otherwise stated a restriction that does not
  // exist. What an operator needs flagged is a model in USE, because that is what
  // limits what can be done to it.
  modelInUse: { de: 'In Verwendung', en: 'In use' },
  colStages: { de: 'Phasenfolge', en: 'Stage sequence' },
  colInUse: { de: 'Kulturen', en: 'Crops' },
  noStagesYet: { de: 'Noch keine Phasen', en: 'No stages yet' },
  modelInUseHint: {
    de: 'Kulturen verweisen auf dieses Modell. Statt löschen: auf inaktiv setzen — dann verschwindet es aus der Auswahl, ohne bestehende Kulturen zu verwaisen.',
    en: 'Crops reference this model. Set it inactive instead of deleting — it leaves the picker without orphaning the crops that already use it.',
  },
  confirmDeleteModel: {
    de: 'Modell {code} samt Phasenliste löschen?',
    en: 'Delete model {code} and its stage list?',
  },
  modelCodeHint: {
    de: 'Kleinbuchstaben, Ziffern und _ — die Kennung, die API, Site-Replik und CSV-Import sprechen.',
    en: 'Lowercase, digits and _ — the identity the API, the site replica and the CSV importers all speak.',
  },
  modelCodeFixed: {
    de: 'Nach dem Anlegen fest: jede Kultur und jedes Rezept verweist darauf.',
    en: 'Fixed after creation: every crop and recipe references it.',
  },
  modelActiveHint: {
    de: 'Inaktive Modelle stehen für NEUE Kulturen nicht mehr zur Auswahl; bestehende laufen unverändert weiter.',
    en: 'An inactive model is no longer offered for NEW crops; the ones already using it are unaffected.',
  },
  modelStagesHint: {
    de: 'Nur Name und Reihenfolge — die Zeilenfolge IST die Phasenfolge. Raum und Dauer gehören ins REZEPT der Kultur, nicht hierher: das Modell bleibt allgemein. Eine Phase, die ein bestehendes Rezept schon nutzt, kann nicht entfernt werden.',
    en: 'Name and order only — the row order IS the stage order. Which room a stage sits in and how long it runs belong to the crop RECIPE, not here: the model stays generic. A stage an existing recipe already uses cannot be removed.',
  },
  stageCodeHint: { de: 'Kleinbuchstaben, Ziffern und _', en: 'Lowercase, digits and _' },

  // ── R-251: the model owns the stage list; the recipe owns light ──
  stageFromModelHint: {
    de: 'Nur Phasen dieses Anbaumodells. Welche Phasen es überhaupt gibt, wird am MODELL festgelegt; die Reihenfolge und Dauer hier — eine Phase darf mehrfach vorkommen (Schnitt · Nachwachsen · Schnitt …).',
    en: 'Only stages of this cultivation model. WHICH stages exist is set on the MODEL; the order and length are set here — and a stage may appear more than once (cut · regrowth · cut …).',
  },
  colLightIntensity: { de: 'Lichtintensität (%)', en: 'Light intensity (%)' },
  lightIntensityHint: {
    de: 'Angeforderte Leuchtenleistung, 1–100 %.',
    en: 'Commanded fixture output, 1–100 %.',
  },
  lightIntensityDarkHint: {
    de: 'Dunkelphase — ohne Lichtfenster gibt es keine Leistung zu setzen.',
    en: 'Blackout — with no light window there is no output to set.',
  },
  helpLightIntensity: {
    de: 'Was die Leuchte LEISTEN SOLL, in Prozent (0 = aus). Nicht zu verwechseln mit PPFD: das ist der GEMESSENE Wert und hat eigene Alarmgrenzen — erst beide zusammen zeigen, ob die Anforderung auch ankommt. Die heutige Hardware schaltet nur ein/aus und behandelt jeden Wert über 0 als „ein"; die echte Zahl greift, sobald ein dimmbarer Treiber das Relais ersetzt.',
    en: "What the fixture is ASKED to deliver, in percent (0 = off). Not the same as PPFD, which is the MEASURED value with its own alarm limits — the two together are what tell you the ask actually landed. Today's hardware only switches on and off and treats any value above 0 as “on”; the real number starts mattering once a dimmable driver replaces the relay.",
  },
  addRepeatStage: { de: '{stage} anhängen', en: 'Add another {stage}' },
  colPool: { de: 'Kapazitätspool', en: 'Capacity pool' },
  poolSeedingRoom: { de: 'Aussaatraum', en: 'Seeding room' },
  poolGerminationRoom: { de: 'Keimraum', en: 'Germination room' },
  poolGrowRoom: { de: 'Grow Room', en: 'Grow room' },
  poolNone: { de: 'keiner', en: 'none' },
  bands: { de: 'Schwellenbänder', en: 'Threshold bands' },
  colParameter: { de: 'Parameter', en: 'Parameter' },
  colPhase: { de: 'Licht', en: 'Light' },
  phaseOn: { de: 'AN', en: 'ON' },
  phaseOff: { de: 'AUS', en: 'OFF' },
  colCritLow: { de: 'Krit. niedrig', en: 'Crit low' },
  colOrangeLow: { de: 'Orange niedrig', en: 'Orange low' },
  colOptimal: { de: 'Optimal', en: 'Optimal' },
  colOrangeHigh: { de: 'Orange hoch', en: 'Orange high' },
  colCritHigh: { de: 'Krit. hoch', en: 'Crit high' },
  notApplicable: { de: 'nicht anwendbar', en: 'not applicable' },

  // ── the merged stage-plan editor (cycle → stage → sub-stage, 2026-08-27) ──
  // One screen replacing the Stage plan tab and the Alarm limits tab: the same
  // stage was being described in two places an operator had to reconcile.
  //
  // ⚠ "Light", and the row's own switch IS THE LAMP (R-323, 2026-08-29,
  // user-designed after asking twice why both existed).
  //
  // It was briefly renamed "Light level" to distinguish it from a separate Lamp
  // switch. That switch is gone: every row now means one thing — *do we want this
  // during this sub-stage?* — and for light that reads **enabled = the lamp is on,
  // disabled = blackout**. The µmol ladder beside it is what the room is judged
  // against while it is on, and stating one is OPTIONAL: "on, no target" is a real
  // answer that 261 live blocks give.
  //
  // ⚠ Switching this row off is not like switching the others off. For a merely
  // watched parameter it stops an alarm; here it DARKENS THE BLOCK. Same gesture,
  // much larger consequence — which is why `spLightHint` says so out loud.
  //
  // A dark block is still watched for a light LEAK, by a detector that builds its
  // own band and never reads this row.
  spLight: { de: 'Licht', en: 'Light' },
  spLightHint: {
    de: 'An = Lampe an für diesen Abschnitt, mit dem Bereich darunter als Sollwert. Aus = Verdunkelung.',
    en: 'On = the lamp runs for this block, judged against the range beside it. Off = blackout.',
  },
  spHint: {
    de: 'Eine Phase ist ein Abschnitt des Kulturzyklus. Jede Unterphase hält konstante Bedingungen; Dauern summieren sich nach oben.',
    en: 'A stage is a chapter of the crop cycle. Each sub-stage holds constant conditions; durations add up.',
  },
  spSubStages: { de: 'Unterphasen', en: 'Sub-stages' },
  spAddSubStage: { de: 'Unterphase hinzufügen', en: 'Add sub-stage' },
  spDuplicate: { de: 'Duplizieren', en: 'Duplicate' },
  // A repeating pattern of blocks, drawn once with a count. Presentation only —
  // the plan underneath stays expanded, which is why an edit says how far it reaches.
  spBlockTitle: { de: 'Block {i} von {n}', en: 'Block {i} of {n}' },
  spPickBlock: {
    de: 'Wählen Sie links einen Block, um seine Werte zu bearbeiten.',
    en: 'Pick a block on the left to edit its values.',
  },
  spDuration: { de: 'Dauer', en: 'Duration' },
  // ── R-369: irrigation lives on the block ────────────────────────────────────
  // The block's OWN watering row — distinct from `spIrrigation` (the stage's
  // watering MODE, above it in the stage panel): this states duration × repeat,
  // on the crop's own minute clock, same as the block's own duration above it.
  spIrrigationRunDuration: { de: 'Laufzeit', en: 'Run duration' },
  spIrrigationRepeat: { de: 'Wiederholungen', en: 'Repeat' },
  spIrrigationBlockDisabledHint: {
    de: 'Bewässerung der Phase zuerst festlegen.',
    en: "Set the stage's irrigation first.",
  },
  // `{times}` arrives already joined ("0 h, 6 h") — the words around each figure
  // are `showDuration`'s, already localised, so composing the LIST here rather
  // than as a template avoids saying the unit twice.
  spIrrigationRunsAt: { de: 'Läuft um {times}', en: 'runs at {times}' },
  spIrrigationRunsOnce: { de: '1 Durchlauf zu Blockbeginn', en: '1 run at block start' },
  spIrrigationAnd: { de: 'und', en: 'and' },
  spIssueIrrigationMinutesMin: {
    de: 'Laufzeit muss mindestens 1 Minute sein',
    en: 'Run duration must be at least 1 minute',
  },
  spIssueIrrigationRepeatMin: {
    de: 'Wiederholungen müssen mindestens 1 sein',
    en: 'Repeat must be at least 1',
  },
  spIssueIrrigationExceedsBlock: {
    de: 'Läuft länger, als der Block dauert',
    en: 'Runs longer than the block lasts',
  },
  spIssueIrrigationNeedsMode: {
    de: 'Die Phase sagt noch nicht, wie sie bewässert wird — dort zuerst festlegen',
    en: 'The stage states no watering yet — set it there first',
  },
  // 2026-09-22: a wet stage (ebb_flood/drip/mist) must state a run somewhere —
  // per-block "off" is still fine once at least one block carries one.
  spIssueIrrigationNoneStated: {
    de: 'Diese Phase wird bewässert, aber kein Block nennt einen Lauf — auf mindestens einem Block eine Laufzeit angeben oder die Bewässerung auf "keine" setzen',
    en: 'This stage is watered but no block states a run — add a duration on at least one block or set irrigation to none',
  },
  // The per-PROFILE band. These three were the Recipe tab; measured against live
  // data, none of them has ever varied by stage, so they are stated once.
  spProfileWide: { de: 'Gilt für das ganze Profil', en: 'Applies to the whole profile' },
  spPlanSaved: { de: 'Plan gespeichert', en: 'Plan saved' },
  spDiscardTitle: { de: 'Planänderungen verwerfen?', en: 'Discard plan changes?' },
  spDiscardMsg: {
    de: 'Alle seit dem letzten Speichern geänderten Phasen, Blöcke und Grenzwerte gehen verloren. Der Plan kehrt zum gespeicherten Stand zurück.',
    en: 'Every stage, block and limit changed since the last save is lost. The plan goes back to what is stored.',
  },
  spDiscard: { de: 'Verwerfen', en: 'Discard' },
  spRemoveStageTitle: { de: 'Phase „{stage}“ entfernen?', en: 'Remove stage “{stage}”?' },
  spRemoveStageMsg: {
    de: 'Die Phase wird mit ihren Blöcken ({n}) und allen darin gesetzten Grenzwerten aus dem Entwurf entfernt. Rückgängig machen lässt sich das nicht.',
    en: 'The stage is removed from the draft with its blocks ({n}) and every limit set in them. This cannot be undone.',
  },
  spSpectrum: { de: 'Spektrum', en: 'Spectrum' },
  spSpectrumNote: {
    de: 'Von den verbauten Leuchten bestimmt — keine Phase kann es ändern.',
    en: 'Fixed by the installed fixtures — no stage can vary it.',
  },
  spAirflow: { de: 'Luftstrom', en: 'Airflow' },
  // Two DIFFERENT notes, and they were being shown as one. `spectrum_note` says
  // what the range means ("Blue-weighted PAR"); the profile note is about the
  // version as a whole. Labelled apart so a profile-wide remark stops reading as
  // a comment on the spectrum.
  spSpectrumCustom: { de: 'Eigenes…', en: 'Custom…' },
  spSpectrumCustomName: { de: 'Name des Spektrums', en: 'Name this spectrum' },
  spProfileNotes: { de: 'Profilnotizen', en: 'Profile notes' },
  spProfileNotesHint: {
    de: 'Gilt für diese Version insgesamt.',
    en: 'About this version as a whole.',
  },
  spNm: { de: 'nm', en: 'nm' },
  // Watering: a SCHEDULE, so it sits below the threshold table rather than
  // pretending to be a limit.
  spIrrigation: { de: 'Bewässerung', en: 'Irrigation' },
  spStageSettings: { de: 'Phaseneinstellungen', en: 'Stage settings' },
  spStageHint: { de: 'Aus der Phasenliste der Farm.', en: "From the farm's stage list." },
  spPoolHint: {
    de: 'Welchen Raum diese Phase belegt — danach plant der Scheduler.',
    en: 'Which space this stage occupies — what the scheduler reserves.',
  },
  spIrrigationHint: { de: 'Wie das Wasser zugeführt wird.', en: 'How the water is delivered.' },
  spStageNote: { de: 'Phasennotiz', en: 'Stage note' },
  spStageNotePlaceholder: { de: 'Notiz zu dieser Phase…', en: 'Note about this stage…' },
  // Safety-relevant prose the mode cannot express — "NEVER fertigate during
  // germination — osmotic-stress risk."
  spFertigationNote: { de: 'Düngung', en: 'Feed' },
  spFertigationPlaceholder: {
    de: 'Was im Wasser ist, Handhabungsregeln…',
    en: "What's in the water, handling rules…",
  },
  spParamNote: { de: 'Notiz', en: 'Note' },
  // A repaired value: it does not match its source document and a horticulturist
  // must confirm it before the profile can govern a room.
  spFlagged: { de: 'Freigabe nötig', en: 'Needs sign-off' },
  spFlaggedWhy: {
    de: '{code}: {subject}. Nicht hier freigebbar — die Freigabe gilt für alle betroffenen Profile.',
    en: '{code}: {subject}. Not signed off here — clearing it releases every profile it reaches.',
  },
  spIncomplete: {
    de: '{n} Parameter sind eingeschaltet, aber ohne Grenzwert.',
    en: '{n} parameters are switched on with no limit set.',
  },
  spShowNotes: { de: 'Notizen zeigen', en: 'Show notes' },
  spHideNotes: { de: 'Notizen ausblenden', en: 'Hide notes' },
  spParamNotePlaceholder: { de: 'Warum dieser Wert…', en: 'Why this value…' },
  spIrrigationNone: { de: 'keine', en: 'none' },
  spIrrigationMist: { de: 'Sprühnebel', en: 'mist' },
  spIrrigationEbbFlood: { de: 'Ebbe/Flut', en: 'ebb & flood' },
  spIrrigationDrip: { de: 'Tropf', en: 'drip' },
  spIrrigationUnset: { de: 'nicht angegeben', en: 'not stated' },
  // Says the COUNT and the duration it matches on, because the action overwrites
  // blocks that are off-screen. "Apply to all" with no number is a surprise.
  spApplyToAll: {
    de: 'Auf alle {n} Blöcke mit {d} anwenden',
    en: 'Apply to all {n} blocks of {d}',
  },
  // ── the repeat (R-317) ──────────────────────────────────────────────────
  // A run of sub-stages that goes round several times. "Repeats" rather than
  // "Loop" or "Cycle": a CYCLE already names the whole crop cycle in this
  // console, and reusing the word for a run of blocks inside one stage would
  // make "cycle total" ambiguous on the same screen.
  spRepeats: { de: 'Wiederholt', en: 'Repeats' },
  spRepeatCount: { de: 'Anzahl der Wiederholungen', en: 'Repeat count' },
  // The count is in the label because the run is what disappears — an operator
  // needs to know how many rows they are about to get.
  // Tick the sub-stages, then group them. The verbs are the ones every other
  // tool uses for exactly this gesture, so nothing has to be learned.
  spGroup: { de: 'Gruppieren', en: 'Group' },
  // ⚠ `spLamp`/`spLampHint` are GONE (R-323) — the switch they labelled was a
  // duplicate of the Light row and was removed with it. `spLampOn`/`spLampOff`
  // stay: the chain still shows each block's lamp at a glance, now read off the
  // Light row rather than off a field of its own.
  spLampOn: { de: 'An', en: 'On' },
  spLampOff: { de: 'Aus', en: 'Off' },
  spUngroup: { de: 'Gruppierung aufheben', en: 'Ungroup' },
  spSelectSubStage: { de: 'Unterphase auswählen', en: 'Select sub-stage' },
  // Says what to DO, not what went wrong. A group repeats as a unit, so a gap in
  // the selection would mean the rows in between repeat too — which is a different
  // plan from the one being asked for.
  spGroupNotAdjacent: {
    de: 'Zum Gruppieren müssen die Unterphasen aufeinanderfolgen — wählen Sie benachbarte Zeilen.',
    en: 'A group runs as one unit, so pick sub-stages that sit next to each other.',
  },
  spRepeatAddSubStage: {
    de: 'Unterphase zur Gruppe hinzufügen',
    en: 'Add sub-stage to the group',
  },
  // Said on the block being edited, because a repeated block looks exactly like
  // any other one and editing it changes every pass.
  spRepeatRuns: {
    de: 'Läuft {n}× — jeder Durchlauf nutzt diese Werte.',
    en: 'Runs {n} times — every pass uses these values.',
  },
  spRepeatOnce: { de: 'Läuft einmal.', en: 'Runs once.' },
  spCycleTotal: { de: 'Zyklus gesamt', en: 'Cycle total' },
  // ── the framed editor (design "5 · Profile Stage-Plan Editor", 2026-10-05) ──
  // The settings band folds its fields away behind one toggle. "Show" on a
  // read-only version: "Edit" over fields that all refuse input would lie.
  spSettingsTitle: { de: 'Profileinstellungen', en: 'Profile settings' },
  spEditSettings: { de: 'Einstellungen bearbeiten', en: 'Edit settings' },
  spShowSettings: { de: 'Einstellungen zeigen', en: 'Show settings' },
  spHideSettings: { de: 'Einstellungen ausblenden', en: 'Hide settings' },
  spNotStated: { de: 'nicht angegeben', en: 'not stated' },
  spStagesAndBlocks: { de: 'Phasen & Blöcke', en: 'Stages & blocks' },
  // One block of a rhythm — "16 h on". `{d}` arrives already localised.
  spPatternOn: { de: '{d} an', en: '{d} on' },
  spPatternOff: { de: '{d} aus', en: '{d} off' },
  // `StagePlanSummary` — the modal's header facts and footer total. Phrased as
  // "label: n" so no count needs a plural form.
  spCycleFacts: { de: '{d} Zyklus', en: '{d} cycle' },
  spStageCount: { de: 'Phasen: {n}', en: 'Stages: {n}' },
  spOpenIssues: { de: 'Offene Probleme: {n}', en: 'Open issues: {n}' },
  spUnsignedFlags: { de: 'Ohne Freigabe: {n}', en: 'Unsigned flags: {n}' },
  spNoStages: {
    de: 'Noch keine Phasen. Fügen Sie die erste hinzu, um den Zyklus aufzubauen.',
    en: 'No stages yet. Add the first one to start building the cycle.',
  },
  // Storage is ALWAYS minutes; these are display units only.
  spUnitDay: { de: 'Tage', en: 'days' },
  spUnitHour: { de: 'Std.', en: 'hours' },
  spUnitMinute: { de: 'Min.', en: 'minutes' },
  // The short forms, for the list rows where a duration sits next to its number.
  spUnitDayShort: { de: 'T', en: 'd' },
  spUnitHourShort: { de: 'Std', en: 'h' },
  spUnitMinuteShort: { de: 'Min', en: 'min' },
  // ⚠ "On", and it was "Monitored" until R-323. The word changed because the
  // MEANING did: the switch used to say only "is this judged", and it now says
  // *do we want this parameter during this sub-stage* — which for `light` is a
  // command to the fixture, and for a driven parameter is a command not to drive
  // it. "Monitored" described half of that and misdescribed the other half.
  //
  // It also has to FIT: "MONITORED" overflowed the 4.5rem switch column and
  // painted over the next header (reported as `MONITOREDCRIT LOW`).
  spColOn: { de: 'An', en: 'On' },
  // The optimal pair heads two columns of the ladder. Cultivation's own entries
  // rather than `common.min`/`common.max`, so all six rungs come from ONE
  // catalogue — a header row assembled from two of them cannot be typed as a
  // single list, and a mislabelled critical column is a safety problem here.
  spColMin: { de: 'Min', en: 'Min' },
  spColMax: { de: 'Max', en: 'Max' },
  // What a collapsed sub-stage says about itself, so the list can be read without
  // opening every row.
  spNothingSet: { de: 'nichts gesetzt', en: 'nothing set' },
  spIssueNoStage: { de: 'Phase wählen', en: 'Pick a stage' },
  spIssueNoBounds: { de: 'eingeschaltet, aber ohne Grenzwert', en: 'switched on with no limit set' },
  spPickStage: { de: 'Phase wählen…', en: 'Pick a stage…' },
  spIssueZero: { de: 'Dauer muss über 0 liegen', en: 'Duration must be above 0' },
  spIssueLadder: { de: 'Grenzwerte sind nicht aufsteigend', en: 'Limits are not in ascending order' },
  spIssueCount: { de: '{n} zu beheben', en: '{n} to fix' },
  // R-207: a band is not only judged, it DRIVES equipment. Editing an optimal edge
  // changes when a relay switches, so the consequence is shown where the edit is made.
  colDrives: { de: 'Steuert', en: 'Drives' },
  drivesNothing: { de: 'nur Überwachung', en: 'monitored only' },
  drivesAbove: { de: 'ein über {v}', en: 'on above {v}' },
  drivesBelow: { de: 'ein unter {v}', en: 'on below {v}' },
  drivesHint: {
    de: 'Der Rückstellpunkt wird pro Standort aus der Bandbreite und dem gemessenen Sensorrauschen abgeleitet.',
    en: 'The release point is derived per site from the band width and the measured sensor noise.',
  },
  actExhaustFan: { de: 'Abluftventilator', en: 'Exhaust fan' },
  actAcCooler: { de: 'Klimagerät', en: 'AC' },
  actCo2Valve: { de: 'CO₂-Ventil', en: 'CO₂ valve' },
  skipped: { de: 'übersprungen', en: 'skipped' },
  repaired: { de: 'korrigiert', en: 'repaired' },
  repairedHint: {
    de: 'Weicht bewusst vom Quelldokument ab — siehe Datenkonflikt {code}.',
    en: 'Deliberately differs from the source document — see data flag {code}.',
  },
  editBands: { de: 'Bänder bearbeiten', en: 'Edit bands' },
  bandsSaved: { de: 'Bänder gespeichert', en: 'Bands saved' },
  applicableHint: {
    de: 'Nicht anwendbar heißt: die Engine überspringt den Parameter — sie greift NIE auf die Bänder der anderen Lichtphase zurück.',
    en: 'Not applicable means the engine SKIPS the parameter — it never falls back to the other light phase’s bands.',
  },
  bandOrderHint: {
    de: 'Grenzen sind Obergrenzen und müssen aufsteigend bleiben; Überlappungen werden abgelehnt.',
    en: 'Bounds are upper bounds and must stay ascending; overlapping bands are rejected.',
  },
  draftOnly: {
    de: 'Nur Entwürfe können geändert werden — ein aktives Profil steuert reale Räume. Für Änderungen eine neue Version anlegen.',
    en: 'Only a draft can be edited — an active profile governs real rooms. Create a new version to change it.',
  },

  // Capacity tab
  colScope: { de: 'Geltungsbereich', en: 'Scope' },
  scopeModel: { de: 'Anbaumodell', en: 'Model' },
  scopeCrop: { de: 'Kultur', en: 'Crop' },
  scopeVariety: { de: 'Sorte', en: 'Variety' },
  scopeRoom: { de: 'Raum', en: 'Room' },
  colRoom: { de: 'Raum', en: 'Room' },
  colLayers: { de: 'Nutzbare Ebenen', en: 'Usable layers' },
  colTraysPerLayer: { de: 'Tabletts/Ebene', en: 'Trays/layer' },
  colSitesPerTray: { de: 'Plätze/Tablett', en: 'Sites/tray' },
  colPlantsPerRack: { de: 'Pflanzen/Regal', en: 'Plants/rack' },
  colLid: { de: 'Deckel', en: 'Lid' },
  colSubstrate: { de: 'Substrat', en: 'Substrate' },
  addSpec: { de: 'Kapazität hinzufügen', en: 'Add capacity' },
  editSpec: { de: 'Kapazität bearbeiten', en: 'Edit capacity' },
  specHint: {
    de: 'Ein Eintrag nennt nur die Werte, die er ändert; alles andere wird vom nächst-allgemeineren Eintrag geerbt. Ist ein Wert nirgends gesetzt, ist das ein Fehler — keine Vorgabe.',
    en: 'A spec states only the numbers it changes; everything else is inherited from the next broader spec. A value nobody sets is an error, never a default.',
  },
  resolveTitle: { de: 'Kapazität prüfen', en: 'Check capacity' },
  resolve: { de: 'Auflösen', en: 'Resolve' },
  resolveHint: {
    de: 'Zeigt, welche Kapazität diese Kultur in diesem Raum tatsächlich erhält.',
    en: 'Shows what capacity this crop actually gets in this room.',
  },
  physicalLayers: { de: 'Physische Ebenen des Raums', en: 'Room’s physical layers' },
  clamped: { de: 'auf die physischen Ebenen begrenzt', en: 'clamped to the physical layers' },
  traysPerRack: { de: 'Tabletts/Regal', en: 'Trays/rack' },
  sitesPerRack: { de: 'Plätze/Regal', en: 'Sites/rack' },
  deleteSpecTitle: { de: 'Kapazitätseintrag löschen?', en: 'Delete this capacity spec?' },
  deleteSpecMsg: {
    de: 'Fehlt danach eine Auflösung, lehnt die Planung sie ab, statt zu schätzen.',
    en: 'If nothing resolves afterwards, scheduling refuses rather than guessing.',
  },

  // Flags tab
  colFlag: { de: 'Konflikt', en: 'Flag' },
  colSubject: { de: 'Betrifft', en: 'Subject' },
  colConfidence: { de: 'Sicherheit', en: 'Confidence' },
  colSignoff: { de: 'Freigabe', en: 'Sign-off' },
  confHigh: { de: 'hoch', en: 'high' },
  confMedium: { de: 'mittel', en: 'medium' },
  confLow: { de: 'niedrig', en: 'low' },
  signoffRequired: { de: 'Freigabe nötig', en: 'Sign-off required' },
  signedOff: { de: 'Freigegeben', en: 'Signed off' },
  noSignoffNeeded: { de: 'nur dokumentiert', en: 'recorded only' },
  signOff: { de: 'Freigeben', en: 'Sign off' },
  signOffTitle: { de: '{code} freigeben?', en: 'Sign off {code}?' },
  signOffMsg: {
    de: 'Sie bestätigen damit einen Wert, der bewusst vom Quelldokument abweicht. Ihr Name wird auf dem Konflikt vermerkt.',
    en: 'You are confirming a value that knowingly differs from the source document. Your name is recorded on the flag.',
  },
  flagSignedOff: { de: 'Konflikt freigegeben', en: 'Flag signed off' },
  conflictLabel: { de: 'Widerspruch', en: 'Conflict' },
  callLabel: { de: 'Entscheidung', en: 'Call taken' },

  // Import tab
  importTitle: { de: 'Klima-Matrix importieren', en: 'Import the climate matrix' },
  importHint: {
    de: 'CSV des Normalisierers hochladen oder einfügen. Der Import erzeugt Profil-ENTWÜRFE — aktiv wird nur, was jemand freigibt.',
    en: 'Upload or paste the normaliser’s CSV. An import creates profile DRAFTS — nothing goes live without a sign-off.',
  },
  chooseFile: { de: 'CSV wählen', en: 'Choose CSV' },
  pasteCsv: { de: 'CSV einfügen', en: 'Paste CSV' },
  dryRun: { de: 'Testlauf', en: 'Dry run' },
  runImport: { de: 'Import starten', en: 'Run import' },
  matrixImportTitle: { de: 'Klimamatrix-Import', en: 'Climate-matrix import' },
  orPasteBelow: { de: 'oder unten einfügen', en: 'or paste below' },
  orWord: { de: 'oder', en: 'or' },
  uploadCorrectedCsv: { de: 'Korrigierte CSV hochladen…', en: 'Upload a corrected CSV…' },
  loadedChars: { de: '{n} Zeichen geladen', en: '{n} characters loaded' },
  reportProfiles: { de: 'Profile geschrieben', en: 'Profiles written' },
  reportStages: { de: 'Phasen geschrieben', en: 'Stages written' },
  reportBands: { de: 'Bänder geschrieben', en: 'Bands written' },
  reportRepaired: { de: 'Bänder korrigiert', en: 'Bands repaired' },
  reportMissing: { de: 'Fehlende Kulturen', en: 'Missing crops' },
  reportFlags: { de: 'Angewandte Konflikte', en: 'Flags applied' },
  reportDryRun: {
    de: 'Testlauf — nichts geschrieben. Die Zahlen entsprechen genau dem echten Lauf.',
    en: 'Dry run — nothing was written. The counts are identical to the real run.',
  },
  reportMissingHint: {
    de: 'Für diese Codes wurde NICHTS geschrieben: die Kultur fehlt im Katalog. Erst anlegen (Kategorie + Anbaumodell), dann erneut importieren.',
    en: 'NOTHING was written for these codes — the crop is not in the catalogue. Create it first (category + cultivation model), then import again.',
  },
  imported: { de: 'Matrix importiert', en: 'Matrix imported' },
  importConfirmTitle: { de: 'Import wirklich ausführen?', en: 'Run the import for real?' },
  importConfirmMsg: {
    de: 'Kein Testlauf: Dieser Import schreibt Profil-Entwürfe in den Katalog. Quelle: {file}.',
    en: 'Not a dry run — this import writes crop profile drafts into the catalogue. Source: {file}.',
  },
  importPastedCsv: { de: 'eingefügte CSV', en: 'pasted CSV' },
  noFileChosen: { de: 'Keine Datei gewählt', en: 'No file chosen' },
  // The importer resolves columns BY HEADER LABEL (backend `cultivation_matrix.rs`
  // COLUMNS), so the header line is the one thing worth showing before a paste.
  // Column names are the file's own and stay untranslated.
  csvPlaceholder: {
    de: 'Erste Zeile ist die Kopfzeile:\ncrop_name,day,stage,parameter,light_phase,applicable,unit,crit_low,orange_low,optimal_min,optimal_max,orange_high,crit_high,photoperiod_hours',
    en: 'First line is the header:\ncrop_name,day,stage,parameter,light_phase,applicable,unit,crit_low,orange_low,optimal_min,optimal_max,orange_high,crit_high,photoperiod_hours',
  },

  // ── form placeholders: a realistic example per field ─────────────────────
  // `phExample` carries the numeric ones, whose example needs no translation.
  phExample: { de: 'z. B. {v}', en: 'e.g. {v}' },
  phCategoryName: { de: 'z. B. Küchenkräuter', en: 'e.g. Culinary Herbs' },
  phCropName: { de: 'z. B. Basilikum', en: 'e.g. Sweet Basil' },
  phLatinName: { de: 'z. B. Ocimum basilicum', en: 'e.g. Ocimum basilicum' },
  phVarietyName: { de: 'z. B. Genoveser', en: 'e.g. Genovese' },
  phSupplier: { de: 'z. B. Name des Saatgutlieferanten', en: 'e.g. the seed supplier’s name' },
  phSubstrate: { de: 'z. B. Kokosfaser', en: 'e.g. Coco coir' },
  phLidType: { de: 'z. B. Feuchtigkeitshaube', en: 'e.g. Humidity dome' },
  phNotes: {
    de: 'Optional — was die nächste Person wissen sollte',
    en: 'Optional — anything the next person should know',
  },
  phProfileNotes: {
    de: 'z. B. Versuch mit dem neuen LED-Spektrum',
    en: 'e.g. Trial with the new LED spectrum',
  },
  phWhyThisRow: {
    de: 'z. B. Handbuch S. 12 nennt eine längere Dunkelphase',
    en: 'e.g. Manual p. 12 states a longer dark phase',
  },
  // The disabled "Set up recipe" on a crop with no profile: a disabled control
  // has to say what would enable it.
  recipeNeedsProfileHint: {
    de: 'Diese Kultur hat noch kein Profil — zuerst unter „Profile“ einen Entwurf anlegen.',
    en: 'This crop has no profile yet — create a draft under Profiles first.',
  },

  // Shared
  emptyTitle: { de: 'Noch keine Einträge', en: 'Nothing here yet' },
  emptyMsg: { de: 'Legen Sie den ersten Eintrag an.', en: 'Create the first entry.' },
  loadError: { de: 'Laden fehlgeschlagen', en: 'Could not load' },
  toastSaved: { de: '{code} gespeichert', en: '{code} saved' },
  toastDeleted: { de: '{code} gelöscht', en: '{code} deleted' },
} satisfies Namespace;
